import React, { useState, useContext, useEffect } from 'react';
import { View, StyleSheet, TouchableOpacity, TextInput, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';
import ThemeContext from '../../context/Theme';
import getThemedColor from '../../services/get-themed-color';
import Map from '../Map';
import MyDropDownPicker from '../MyDropDownPicker';
import MyDataTable from '../MyDataTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faBriefcase, faBuilding, faRoute, faSearch, faUser } from '@fortawesome/free-solid-svg-icons';
import { dispatchService } from '../../services/central-api/dispatch';
import moment from 'moment';
import { MyCheckbox } from '../MyCheckbox';

interface UserAndDistances extends User {
    distance?: number;
}

interface UnitAndDistance extends Unit {
    distance?: number;
    isStarter: boolean;
}

interface UnitRow extends UnitAndDistance {
    equipmentExpanded?: boolean;
}

interface UserRow extends UserAndDistances {
    expanded?: boolean;
}

interface DispatchTabParams {
    occurrence: Occurrence;
    agentPositions: AgentPosition[];
    pin?: LatLng;
    dispatchedUnits: Unit[];
    setDispatchedUnits: (value: Unit[]) => void;
}

export default function OccurrenceDetailsDispatchTab({ occurrence, agentPositions, pin, dispatchedUnits, setDispatchedUnits }: DispatchTabParams) {
    const { theme } = useContext(ThemeContext);
    const styles = getStyles(theme);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [, updateState] = React.useState({});
    const forceUpdate = React.useCallback(() => updateState({}), []);

    const [equipmentPickerOpen, setEquipmentPickerOpen] = useState(false);
    const [equipments, setEquipments] = useState<SimplifiedEquipment[]>([]);
    const [skillPickerOpen, setSkillPickerOpen] = useState(false);
    const [skills, setSkills] = useState<SimplifiedSkill[]>([]);
    const [unitsToDispatch, setUnitsToDispatch] = useState<Unit[]>([]);
    const [equipmentFilterList, setEquipmentFilterList] = useState<number[]>([]);
    const [skillFilterList, setSkillFilterList] = useState<number[]>([]);
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [unitFilter, setUnitFilter] = useState('');

    async function getEquipments() {
        try {
            const response = await dispatchService.getSimplifiedEquipments();
            setEquipments(response);
        } catch (err) {
            console.error(err);
        }
    }

    async function getSkills() {
        try {
            const response = await dispatchService.getSimplifiedSkills();
            setSkills(response);
        } catch (err) {
            console.error(err);
        }
    }

    async function getUnits(unitsDispatched: Unit[]) {
        try {
            if (!occurrence?.id) {
                return;
            }

            setIsLoading(true);
            const units = await dispatchService.getUnits({
                equipmentsIds: equipmentFilterList,
                skillsIds: skillFilterList,
                unitName: unitFilter,
                dispatchedUnits: unitsDispatched.map(unit => unit.id)
            });
            for (const unit of units) {
                (unit as UnitAndDistance).isStarter = false;
            }
            setUnitsToDispatch(units);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    async function getDispatchedUnits() {
        try {
            if (!occurrence?.id) {
                return;
            }
            const unitsDispatched = await dispatchService.getOccurrenceDispatchedUnits(occurrence.id);
            for (const unit of unitsDispatched) {
                const foundUnit = unit.OccurrenceUnits?.find(x => x.OccurrenceUnit?.occurrenceId == occurrence.id);
                (unit as UnitAndDistance).isStarter = foundUnit && foundUnit.OccurrenceUnit ? foundUnit.OccurrenceUnit.isStarter : false;
            }
            setDispatchedUnits(unitsDispatched);
            await getUnits(unitsDispatched);
        } catch (err) {
            console.error(err);
        }
    }

    async function getAssignments() {
        try {
            const assignments = await dispatchService.getAssignments({ page: 0, limit: 9999999 });
            setAssignments(assignments.rows);
        } catch (err) {
            console.error(err);
        }
    }

    const renderUnitContent = (unit: UnitRow) => {
        return <View style={{ paddingVertical: 10 }}>
            <MyDataTable
                dense={true}
                noTableHead={true}
                columns={[
                    {
                        cell: row => <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 10 }}>
                            <FontAwesomeIcon icon={faUser} fontSize={16} color={row.agentPosition && moment(row.agentPosition.positionUpdatedAt) > moment().subtract(10, 'minutes') ? 'green' : 'red'} />
                            <MyAppText>{row.warName || row.name}</MyAppText>
                        </View>,
                        grow: 3
                    },
                    {
                        cell: row => <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 10 }}>
                            <MyAppText>{row.distance}</MyAppText>
                        </View>,
                        width: '100px',
                    },
                    {
                        cell: () => <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 10 }}></View>,
                        width: '76px',
                    }
                ]}
                expandableRows
                expandableRowExpanded={(row) => row.expanded || false}
                onRowExpandToggled={(bool, row) => row.expanded = bool}
                expandableRowsComponent={({ data }) => renderAgentInformation(data || [], unit)}
                data={(unit.users || []) as UserRow[]}
            />
            {unit.equipments?.length ? <MyDataTable
                dense={true}
                noTableHead={true}
                columns={[
                    {
                        cell: row => <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 10 }}>
                            <FontAwesomeIcon icon={faBriefcase} fontSize={16} color={getThemedColor(theme, '#888888')} />
                            <MyAppText>{row.name}</MyAppText>
                        </View>,
                    },
                ]}
                expandableRows
                expandableRowExpanded={() => unit.equipmentExpanded || false}
                onRowExpandToggled={(bool) => {
                    unit.equipmentExpanded = bool;
                }}
                expandableRowsComponent={({ data }) => renderEquipments(data.equipments || [])}
                data={[{ name: translate('equipments'), equipments: unit.equipments }]}
            /> : null}

        </View>;
    };

    const renderAgentInformation = (user: User, unit: Unit) => {
        const userSkills = user.Skills.map(skill => skill.name);
        let userAssignment;
        if (unit) {
            const unitUser = unit.UnitUsers?.find(uu => uu.id == user.id);
            if (unitUser) {
                userAssignment = assignments.find((assignment) => assignment.id == unitUser.UnitUser.assignmentId);
            }
        }

        return <View style={{ paddingLeft: 96 }}>
            <View style={{ flexDirection: 'row', columnGap: 5, minHeight: 32, alignItems: 'center' }}>
                <MyAppText style={{ fontWeight: 'bold' }}>{translate('skills')}:</MyAppText>
                {userSkills.length ?
                    <MyAppText>{userSkills.join(',')}</MyAppText>
                    : <MyAppText>{translate('noSkills')}</MyAppText>
                }
            </View>
            <View style={{ flexDirection: 'row', columnGap: 5, minHeight: 32, alignItems: 'center' }}>
                <MyAppText style={{ fontWeight: 'bold' }}>{translate('assignment')}:</MyAppText>
                {userAssignment ?
                    <MyAppText>{userAssignment.name}</MyAppText>
                    : <MyAppText>{translate('notDefined')}</MyAppText>
                }
            </View>
        </View>;
    };

    const renderEquipments = (equipments: Equipment[]) => {
        return <View style={{ paddingLeft: 80 }}>
            <MyDataTable
                dense={true}
                noTableHead={true}
                columns={[
                    {
                        selector: row => row.name,
                        grow: 2
                    },
                ]}
                data={equipments}
            />
        </View>;
    };

    function toRadians(degrees: number) {
        return degrees * (Math.PI / 180);
    }

    function haversineDistance(location1: LatLng, location2: LatLng) {
        const deltaLatitude = toRadians(location2.lat - location1.lat);
        const deltaLongitude = toRadians(location2.lng - location1.lng);
        const haversineComponent = Math.sin(deltaLatitude / 2) ** 2 +
            Math.cos(toRadians(location1.lat)) * Math.cos(toRadians(location2.lat)) *
            Math.sin(deltaLongitude / 2) ** 2;
        const earthRadiusKm = 6371 * 2;
        const distance = earthRadiusKm * Math.asin(Math.sqrt(haversineComponent));
        return parseFloat(distance.toFixed(1));
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == "Enter") {
            getUnits(dispatchedUnits);
        }
    }

    useEffect(() => {
        getEquipments();
        getSkills();
        getAssignments();
        getDispatchedUnits();
    }, []);

    function updateUnitAgentPositions(pin: LatLng, units: UnitAndDistance[]) {
        for (const unit of units) {
            let distance = Infinity;
            for (const user of unit.users as UserAndDistances[]) {
                const agentPosition = agentPositions.find(ap => ap.userId == user.id);
                if (!agentPosition) {
                    continue;
                }
                user.distance = haversineDistance(pin, {
                    lat: Number(agentPosition.latitude),
                    lng: Number(agentPosition.longitude),
                });
                if (user.distance < distance) {
                    distance = user.distance;
                }
            }
            unit.distance = distance;
        }
    }

    useEffect(() => {
        if (!pin) {
            return;
        }
        updateUnitAgentPositions(pin, dispatchedUnits as UnitAndDistance[]);
        updateUnitAgentPositions(pin, unitsToDispatch as UnitAndDistance[]);
        forceUpdate();
    }, [pin, dispatchedUnits, agentPositions, unitsToDispatch]);

    return (
        <View style={styles.container}>
            <View style={[styles.tableFilters, { zIndex: 2 }]}>
                <View style={{ flex: 1, minWidth: 300 }}>
                    <TextInput
                        style={[styles.filterInput, {
                            color: unitFilter ? getThemedColor(theme, '#222222') : getThemedColor(theme, '#CBCBCB')
                        }]}
                        value={unitFilter}
                        onChangeText={setUnitFilter}
                        placeholder={translate('searchUnit')}
                        onKeyPress={handleKeyDown}
                    />
                </View>
                <View style={{ flex: 1, minWidth: 250, zIndex: 2 }}>
                    <MyDropDownPicker
                        placeholder={translate('selectEquipments')}
                        multiple={true}
                        mode='BADGE'
                        open={equipmentPickerOpen}
                        onOpen={() => setSkillPickerOpen(false)}
                        setOpen={setEquipmentPickerOpen}
                        value={equipmentFilterList}
                        setValue={setEquipmentFilterList}
                        items={equipments.map((item: SimplifiedEquipment) => {
                            return {
                                label: item.name, value: item.id
                            };
                        })}
                        searchable={true}
                    />
                </View>
                <View style={{ flex: 1, minWidth: 250, zIndex: 1 }}>
                    <MyDropDownPicker
                        placeholder={translate('selectSkills')}
                        multiple={true}
                        mode='BADGE'
                        open={skillPickerOpen}
                        onOpen={() => setEquipmentPickerOpen(false)}
                        setOpen={setSkillPickerOpen}
                        value={skillFilterList}
                        setValue={setSkillFilterList}
                        items={skills.map((item: SimplifiedSkill) => {
                            return {
                                label: item.name, value: item.id
                            };
                        })}
                        searchable={true}
                    />
                </View>
                <TouchableOpacity style={styles.search} onPress={() => getUnits(dispatchedUnits)}>
                    <FontAwesomeIcon icon={faSearch} fontSize={16} color={getThemedColor(theme, '#FFFFFF')} />
                    <MyAppText style={styles.searchText}>{translate('toSearch')}</MyAppText>
                </TouchableOpacity>
            </View>
            <View style={{ flex: 1, flexDirection: 'row' }}>
                <View style={styles.leftArea}>
                    <View style={[styles.table, { rowGap: 20 }]}>
                        <View style={styles.table}>
                            <View style={styles.tableFilters}>
                                <MyAppText style={styles.optionsButtonText}>
                                    {translate('availableUnits')}
                                </MyAppText>
                            </View>
                            <View style={{ zIndex: -1, flex: 1 }}>
                                <MyDataTable
                                    columns={[
                                        {
                                            name: translate('team'),
                                            selector: row => row.unitName.name || '',
                                            wrap: true,
                                            grow: 3
                                        },
                                        {
                                            name: <FontAwesomeIcon icon={faBuilding} fontSize={16} color={getThemedColor(theme, '#888888')} />,
                                            selector: row =>
                                                `${row.actingBodyUnit?.actingBody?.name || ''}/${row.actingBodyUnit?.name || ''}`,
                                            wrap: true,
                                            center: true,
                                        },
                                        {
                                            center: true,
                                            name: <View style={{ flexDirection: 'row', columnGap: 5 }}>
                                                <FontAwesomeIcon icon={faRoute} fontSize={16} color={getThemedColor(theme, '#888888')} />
                                                <MyAppText>(km)</MyAppText>
                                            </View>,
                                            selector: row => row.distance != Infinity && row.distance ? row.distance : '',
                                            sortable: true,
                                            width: '100px',
                                        },
                                        {
                                            button: true,
                                            cell: row => {
                                                const idx = unitsToDispatch.findIndex((el) => el.id == row.id);

                                                return <TouchableOpacity onPress={() => {
                                                    unitsToDispatch.splice(idx, 1);
                                                    if (!dispatchedUnits.length) {
                                                        row.isStarter = true;
                                                    }
                                                    dispatchedUnits.push(row);
                                                    setUnitsToDispatch([...unitsToDispatch]);
                                                    setDispatchedUnits([...dispatchedUnits]);
                                                }} style={[styles.button]}>
                                                    <FontAwesomeIcon fontSize={16} icon={faArrowDown} />
                                                </TouchableOpacity>;
                                            },
                                            width: '100px'
                                        }
                                    ]}
                                    expandableRows
                                    onSelectedRowsChange={({ selectedRows }) => {
                                        if (selectedRows.length !== unitsToDispatch.length) {
                                            setUnitsToDispatch(selectedRows);
                                        }
                                    }}
                                    expandableRowsComponent={({ data }) => renderUnitContent(data)}
                                    data={(unitsToDispatch as UnitAndDistance[]).sort((a, b) => {
                                        if (a.distance === undefined) return 1;
                                        if (b.distance === undefined) return -1;
                                        return a.distance - b.distance;
                                    })}
                                    progressPending={isLoading}
                                />
                            </View>
                        </View>
                        <View style={styles.table}>
                            <View style={styles.tableFilters}>
                                <MyAppText style={styles.optionsButtonText}>
                                    {translate('dispatchedUnits')}
                                </MyAppText>
                            </View>
                            <View style={{ zIndex: -1, flex: 1 }}>
                                <MyDataTable
                                    columns={[
                                        {
                                            name: translate('team'),
                                            selector: row => row.unitName.name || '',
                                            wrap: true,
                                            grow: 3
                                        },
                                        {
                                            name: '',
                                            cell: row => {
                                                return <MyCheckbox
                                                    style={{}}
                                                    label={row.isStarter ? translate('starter') : translate('support')}
                                                    checked={row.isStarter}
                                                    setChecked={() => {
                                                        for (const unit of dispatchedUnits as UnitAndDistance[]) {
                                                            unit.isStarter = false;
                                                        }
                                                        row.isStarter = true;
                                                        forceUpdate();
                                                    }}
                                                />;
                                            },
                                            wrap: true,
                                            center: true,
                                        },
                                        {
                                            name: <FontAwesomeIcon icon={faBuilding} fontSize={16} color={getThemedColor(theme, '#888888')} />,
                                            selector: row =>
                                                `${row.actingBodyUnit?.actingBody?.name || ''}/${row.actingBodyUnit?.name || ''}`,
                                            wrap: true,
                                            center: true,
                                        },
                                        {
                                            center: true,
                                            name: <View style={{ flexDirection: 'row', columnGap: 5 }}>
                                                <FontAwesomeIcon icon={faRoute} fontSize={16} color={getThemedColor(theme, '#888888')} />
                                                <MyAppText>(km)</MyAppText>
                                            </View>,
                                            selector: row => row.distance != Infinity && row.distance ? row.distance : '',
                                            sortable: true,
                                            width: '100px',
                                        },
                                        {
                                            button: true,
                                            cell: row => {
                                                if (row.isStarter) {
                                                    return <></>;
                                                }

                                                const idx = dispatchedUnits.findIndex((el) => el.id == row.id);

                                                return <TouchableOpacity onPress={() => {
                                                    dispatchedUnits.splice(idx, 1);
                                                    unitsToDispatch.push(row);
                                                    setUnitsToDispatch([...unitsToDispatch]);
                                                    setDispatchedUnits([...dispatchedUnits]);
                                                }} style={[styles.button]}>
                                                    <FontAwesomeIcon fontSize={16} icon={faArrowUp} />
                                                </TouchableOpacity>;
                                            },
                                            width: '100px'
                                        }
                                    ]}
                                    expandableRows
                                    onSelectedRowsChange={({ selectedRows }) => {
                                        if (selectedRows.length !== dispatchedUnits.length) {
                                            setDispatchedUnits(selectedRows);
                                        }
                                    }}
                                    expandableRowsComponent={({ data }) => renderUnitContent(data)}
                                    data={(dispatchedUnits as UnitAndDistance[]).sort((a, b) => {
                                        if (a.distance === undefined) return 1;
                                        if (b.distance === undefined) return -1;
                                        return a.distance - b.distance;
                                    })}
                                    progressPending={isLoading}
                                />
                            </View>
                        </View>
                    </View>
                </View>
                <Map pinnedMarker={pin} agentPositions={agentPositions} />
            </View>
        </View >);
}

function getStyles(theme: Theme) {
    return StyleSheet.create({
        container: {
            flex: 1,
            backgroundColor: getThemedColor(theme, '#FFFFFF'),
            borderRadius: 4,
            borderWidth: 1,
            borderColor: getThemedColor(theme, '#E6E6E6'),
            padding: 10,
            gap: 5,
        },
        leftArea: {
            flex: 1,
            flexDirection: 'column',
            flexGrow: 2
        },
        table: {
            flex: 1,
        },
        tableFilters: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap'
        },
        optionsButtonText: {
            color: getThemedColor(theme, '#58595B'),
            fontSize: 16,
            fontWeight: 'bold',
        },
        button: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: 85,
            height: 25,
            backgroundColor: getThemedColor(theme, '#FFFFFF'),
        },
        filterText: {
            color: getThemedColor(theme, '#58595B'),
            fontSize: 16,
            height: 25,
        },
        filterInput: {
            fontFamily: 'Open Sans',
            backgroundColor: getThemedColor(theme, '#FFFFFF'),
            borderWidth: 1,
            borderRadius: 4,
            borderColor: getThemedColor(theme, '#CCCCCC'),
            height: 40,
            minHeight: 40,
            fontSize: 16,
            color: getThemedColor(theme, '#222222'),
            padding: 10
        },
        search: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 30,
            height: 40,
            backgroundColor: getThemedColor(theme, '#000028'),
            borderRadius: 2,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
        },
        searchText: {
            color: getThemedColor(theme, '#FFFFFF'),
            fontSize: 16,
            paddingLeft: 10
        },
    });
}
